import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Handshake,
  HardHat,
  HeartPulse,
  Landmark,
  LineChart,
  Mail,
  Megaphone,
  Palmtree,
  Sprout,
  Users,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import CezBanner from "../../components/cez/CezBanner";
import CezMiembrosCarousel from "../../components/cez/CezMiembrosCarousel";
import CezAlianzasSection from "../../components/cez/CezAlianzasSection";
import CezNoticiasSection from "../../components/cez/CezNoticiasSection";
import { useEmpresasLanguage } from "../../context/EmpresasLanguageContext";
import {
  consejoEmpresarialSumapaz,
  CEZ_EMAIL,
  CEZ_PATH,
  CEZ_WHATSAPP_AFILIACION,
  CEZ_WHATSAPP_CAPITULO,
} from "../../data/consejoEmpresarialSumapazData";
import { cezNoticias, getCezNoticia } from "../../data/cezNoticias";
import { cezSectorDetalle, getCezSectorDetalle } from "../../data/cezSectorDetalle";
import bannerInicio from "../../assets/cez/cez-banner-inicio.jpg";
import bannerQuienes from "../../assets/cez/cez-banner-quienes-somos.jpg";
import bannerSectores from "../../assets/cez/cez-banner-sectores.jpg";
import bannerCapitulo from "../../assets/cez/cez-banner-fusagasuga-plateada.jpg";
import bannerPlan from "../../assets/cez/cez-banner-el-plan.jpg";
import bannerGobierno from "../../assets/cez/cez-banner-organizacion.jpg";
import bannerAfiliacion from "../../assets/cez/cez-banner-afiliacion.jpg";
import sectorSaludImg from "../../assets/cez/cez-sector-salud.jpg";
import sectorConstruccionImg from "../../assets/cez/cez-sector-construccion.jpg";
import sectorTurismoImg from "../../assets/cez/cez-sector-turismo.jpg";
import sectorAgroImg from "../../assets/cez/cez-sector-agroindustria.jpg";
import sectorSaludDetalleImg from "../../assets/cez/cez-sector-salud-detalle.jpg";
import sectorConstruccionDetalleImg from "../../assets/cez/cez-sector-construccion-detalle.jpg";
import sectorTurismoDetalleImg from "../../assets/cez/cez-sector-turismo-detalle.jpg";
import sectorAgroDetalleImg from "../../assets/cez/cez-sector-agroindustria-detalle.jpg";
import pilarVozImg from "../../assets/cez/cez-pilar-una-sola-voz.jpg";
import pilarNegociosImg from "../../assets/cez/cez-pilar-negocios-entre-socios.jpg";
import pilarInfoImg from "../../assets/cez/cez-pilar-informacion-para-decidir.jpg";

const sectorIcons = {
  salud: HeartPulse,
  construccion: HardHat,
  turismo: Palmtree,
  agroindustria: Sprout,
};

const sectorImages = {
  salud: sectorSaludImg,
  construccion: sectorConstruccionImg,
  turismo: sectorTurismoImg,
  agroindustria: sectorAgroImg,
};

const sectorDetalleImages = {
  salud: sectorSaludDetalleImg,
  construccion: sectorConstruccionDetalleImg,
  turismo: sectorTurismoDetalleImg,
  agroindustria: sectorAgroDetalleImg,
};

const pilarImages = [pilarVozImg, pilarNegociosImg, pilarInfoImg];

const pilarIcons = [Megaphone, Handshake, LineChart];
const gobiernoIcons = [Users, Landmark, Handshake, Sprout, LineChart];

const useCez = () => {
  const { lang } = useEmpresasLanguage();
  return consejoEmpresarialSumapaz[lang] || consejoEmpresarialSumapaz.es;
};

export function CezHome() {
  const c = useCez();
  const b = c.pageBanners.inicio;

  return (
    <div className="overflow-x-hidden bg-slate-50">
      <CezBanner image={bannerInicio} eyebrow={b.eyebrow} title={b.title} subtitle={b.subtitle} />

      <section className="py-14 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="mt-0 text-lg text-slate-600 leading-relaxed max-w-3xl">{c.hero.subtitle}</p>
          <p className="mt-6 max-w-xl border-l-2 border-[#1746a2] pl-5 text-base sm:text-lg italic text-slate-700">
            {c.hero.quote}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={CEZ_WHATSAPP_AFILIACION}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#1746a2] px-7 py-3 font-bold text-white hover:bg-[#123a72] transition"
            >
              {c.hero.ctaAfiliacion}
              <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to={`${CEZ_PATH}/fusagasuga-plateada`}
              className="inline-flex items-center justify-center rounded-full border-2 border-[#1746a2] px-7 py-3 font-bold text-[#1746a2] hover:bg-[#1746a2]/5 transition"
            >
              {c.hero.ctaCapitulo}
            </Link>
          </div>
        </div>
      </section>

      <section className="py-14 px-6 sm:px-10 lg:px-16 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">{c.cifras.label}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0b1f3f]">{c.cifras.title}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{c.cifras.intro}</p>
          </div>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-5 gap-4">
            {c.cifras.items.map((item) => (
              <div key={item.texto} className="rounded-2xl border border-slate-200 bg-white px-5 py-6 text-center">
                <p className="text-3xl sm:text-4xl font-extrabold text-[#1746a2]">{item.valor}</p>
                <p className="mt-2 text-sm text-slate-600 leading-snug">{item.texto}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-slate-500 leading-relaxed">{c.cifras.nota}</p>
        </div>
      </section>

      <CezAgendaSection c={c} />
      <CezNoticiasSection
        title={c.noticias.title}
        intro={c.noticias.intro}
        verMas={c.noticias.verMas}
      />
      <CezCierreSection c={c} />
    </div>
  );
}

export function CezQuienesSomos() {
  const c = useCez();
  const b = c.pageBanners.quienesSomos;

  return (
    <div className="overflow-x-hidden bg-slate-50">
      <CezBanner image={bannerQuienes} eyebrow={b.eyebrow} title={b.title} subtitle={b.subtitle} />
      <section className="py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-slate-600 text-lg leading-relaxed max-w-3xl">{c.proposito.body}</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {c.proposito.pilares.map((pilar, index) => {
              const Icon = pilarIcons[index] || Megaphone;
              const image = pilarImages[index];
              return (
                <article key={pilar.titulo} className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-sm">
                  {image && (
                    <div className="relative h-40 sm:h-44 w-full overflow-hidden">
                      <img
                        src={image}
                        alt=""
                        className="h-full w-full object-cover object-center"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-50/90 via-transparent to-transparent" />
                    </div>
                  )}
                  <div className="p-7">
                    <div className="w-11 h-11 rounded-xl bg-[#1746a2]/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#1746a2]" />
                    </div>
                    <h2 className="text-xl font-extrabold text-[#0b1f3f]">{pilar.titulo}</h2>
                    <p className="mt-2 text-slate-600 leading-relaxed">{pilar.texto}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
      <CezMiembrosCarousel
        label={c.miembros.label}
        title={c.miembros.title}
        intro={c.miembros.intro}
      />
      <CezAlianzasSection title={c.alianzas.title} intro={c.alianzas.intro} />
      <CezNoticiasSection
        title={c.noticias.title}
        intro={c.noticias.intro}
        verMas={c.noticias.verMas}
      />
      <CezCierreSection c={c} />
    </div>
  );
}

export function CezSectores() {
  const c = useCez();
  const b = c.pageBanners.sectores;

  const { lang } = useEmpresasLanguage();
  const sectorLabels = (cezSectorDetalle[lang] || cezSectorDetalle.es).labels;

  return (
    <div className="overflow-x-hidden bg-slate-50">
      <CezBanner image={bannerSectores} eyebrow={b.eyebrow} title={b.title} subtitle={b.subtitle} />
      <section className="py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-slate-600 text-lg leading-relaxed max-w-3xl">{c.sectores.intro}</p>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {c.sectores.items.map((sector) => {
              const Icon = sectorIcons[sector.id] || Handshake;
              const image = sectorImages[sector.id];
              return (
                <article key={sector.id} className="flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-slate-50">
                  {image && (
                    <div className="relative h-44 sm:h-52 w-full overflow-hidden">
                      <img
                        src={image}
                        alt=""
                        className="h-full w-full object-cover object-center"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0b1f3f]/45 via-transparent to-transparent" />
                    </div>
                  )}
                  <div className="flex flex-col p-7 sm:p-8">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 shrink-0 rounded-2xl bg-[#1746a2]/10 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-[#1746a2]" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-extrabold text-[#0b1f3f]">{sector.nombre}</h2>
                        <p className="mt-1 text-sm font-semibold text-[#1746a2]">{sector.lema}</p>
                      </div>
                    </div>
                    <p className="mt-5 flex-1 text-slate-600 leading-relaxed">{sector.porQue}</p>
                    <Link
                      to={`${CEZ_PATH}/sectores/${sector.id}`}
                      className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-[#1746a2] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#123a72] transition"
                    >
                      {sectorLabels.saberMas}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
      <CezCierreSection c={c} />
    </div>
  );
}

export function CezSectorPage() {
  const c = useCez();
  const { lang } = useEmpresasLanguage();
  const { sectorId } = useParams();
  const data = getCezSectorDetalle(lang, sectorId);
  const sector = c.sectores.items.find((item) => item.id === sectorId);

  if (!data || !sector) {
    return <Navigate to={`${CEZ_PATH}/sectores`} replace />;
  }

  const { labels, detalle } = data;
  const Icon = sectorIcons[sectorId] || Handshake;
  const bannerImage = sectorImages[sectorId];
  const detalleImage = sectorDetalleImages[sectorId];

  return (
    <div className="overflow-x-hidden bg-slate-50">
      <CezBanner
        image={bannerImage}
        eyebrow={labels.eyebrow}
        title={sector.nombre}
        subtitle={sector.lema}
      />

      <section className="py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10 lg:gap-14 items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">{labels.contextoLabel}</p>
              <p className="mt-4 text-lg text-slate-700 leading-relaxed font-medium">{detalle.resumen}</p>
              <div className="mt-5 space-y-4">
                {detalle.contexto.map((parrafo) => (
                  <p key={parrafo} className="text-slate-600 leading-relaxed">{parrafo}</p>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
              <p className="text-xs font-bold uppercase tracking-widest text-[#1746a2]">{labels.datosLabel}</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {detalle.datos.map((dato) => (
                  <div key={dato.texto} className="rounded-2xl border border-slate-200 bg-white px-4 py-5 text-center">
                    <p className="text-2xl font-extrabold text-[#1746a2]">{dato.valor}</p>
                    <p className="mt-2 text-xs text-slate-600 leading-snug">{dato.texto}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 sm:px-10 lg:px-16 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">{labels.propuestaLabel}</p>
            <p className="mt-4 text-2xl sm:text-3xl font-extrabold text-[#0b1f3f] leading-snug">{detalle.propuestaIntro}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {detalle.propuesta.map((pilar, index) => (
              <article key={pilar.titulo} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-3xl font-extrabold text-[#1746a2]/25">{String(index + 1).padStart(2, "0")}</p>
                <h2 className="mt-3 text-lg font-extrabold text-[#0b1f3f] leading-snug">{pilar.titulo}</h2>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{pilar.texto}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <img src={detalleImage} alt="" className="h-64 sm:h-80 lg:h-96 w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1f3f]/70 via-[#0b1f3f]/20 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="w-full max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-300 text-[#0b1f3f]">
              <Icon className="h-7 w-7" />
            </div>
            <p className="mt-4 max-w-md text-2xl sm:text-3xl font-extrabold text-white leading-snug">{sector.lema}</p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          <article className="rounded-3xl border border-amber-200 bg-amber-50/60 p-7 sm:p-8">
            <p className="text-xs font-bold uppercase tracking-widest text-amber-700">{labels.retosLabel}</p>
            <ul className="mt-4 space-y-3">
              {sector.retos.map((reto) => (
                <li key={reto} className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                  <p className="text-sm text-slate-700 leading-relaxed">{reto}</p>
                </li>
              ))}
            </ul>
          </article>
          <article className="rounded-3xl border border-[#1746a2]/15 bg-[#1746a2]/5 p-7 sm:p-8">
            <p className="text-xs font-bold uppercase tracking-widest text-[#1746a2]">{labels.mesaLabel}</p>
            <ul className="mt-4 space-y-3">
              {sector.mesa.map((accion) => (
                <li key={accion} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#1746a2]" />
                  <p className="text-sm text-slate-700 leading-relaxed">{accion}</p>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      <section className="py-16 px-6 sm:px-10 lg:px-16 bg-[#1746a2] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold leading-snug">{labels.ctaTitle}</h2>
          <p className="mt-4 text-lg text-[#dbeafe] leading-relaxed">{labels.ctaBody}</p>
          <a
            href={CEZ_WHATSAPP_AFILIACION}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3 font-bold text-[#123a72] hover:bg-[#e8f2ff] transition"
          >
            <FaWhatsapp className="w-4 h-4" />
            {labels.cta}
          </a>
          <div className="mt-8">
            <Link
              to={`${CEZ_PATH}/sectores`}
              className="text-sm font-semibold text-[#dbeafe] hover:text-white hover:underline"
            >
              ← {labels.volver}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export function CezFusagasugaPlateada() {
  const c = useCez();
  const b = c.pageBanners.capitulo;

  return (
    <div className="overflow-x-hidden bg-slate-50">
      <CezBanner image={bannerCapitulo} eyebrow={b.eyebrow} title={b.title} subtitle={b.subtitle} />
      <section className="py-16 px-6 sm:px-10 lg:px-16 bg-[#0f172a] text-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-lg text-slate-300 leading-relaxed max-w-3xl">{c.capitulo.intro}</p>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
            <div>
              <h2 className="text-xl font-extrabold text-amber-300">{c.capitulo.queEsTitle}</h2>
              <div className="mt-4 space-y-4">
                {c.capitulo.queEs.map((parrafo) => (
                  <p key={parrafo} className="text-slate-300 leading-relaxed">{parrafo}</p>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-amber-300">{c.capitulo.porQueTitle}</h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {c.capitulo.porQue.map((item) => (
                  <div key={item.titulo} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <p className="font-bold text-white leading-snug">{item.titulo}</p>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{item.texto}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-14">
            <h2 className="text-xl font-extrabold text-amber-300">{c.capitulo.convergenciaTitle}</h2>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {c.capitulo.convergencia.map((item) => {
                const Icon = sectorIcons[item.id] || Handshake;
                const image = sectorImages[item.id];
                return (
                  <article key={item.id} className="overflow-hidden rounded-2xl border border-amber-300/25 bg-white/5">
                    {image && (
                      <div className="relative h-36 w-full overflow-hidden">
                        <img
                          src={image}
                          alt=""
                          className="h-full w-full object-cover object-center"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/20 to-transparent" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="h-11 w-11 rounded-xl bg-amber-300/15 flex items-center justify-center mb-4">
                        <Icon className="w-5 h-5 text-amber-300" />
                      </div>
                      <p className="text-lg font-extrabold text-white">{item.titulo}</p>
                      <p className="mt-2 text-sm text-slate-300 leading-relaxed">{item.texto}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <div className="mt-12 rounded-3xl border border-amber-300/30 bg-gradient-to-br from-amber-300/10 to-transparent p-8 sm:p-10">
            <p className="text-xs font-bold uppercase tracking-widest text-amber-300">{c.capitulo.ideaSimpleTitle}</p>
            <p className="mt-4 text-xl sm:text-2xl font-bold leading-snug text-white">{c.capitulo.ideaSimple}</p>
            <a
              href={CEZ_WHATSAPP_CAPITULO}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-amber-300 px-7 py-3 font-bold text-[#0f172a] hover:bg-amber-200 transition"
            >
              {c.capitulo.ctaCapitulo}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export function CezElPlan() {
  const c = useCez();
  const b = c.pageBanners.plan;

  return (
    <div className="overflow-x-hidden bg-slate-50">
      <CezBanner image={bannerPlan} eyebrow={b.eyebrow} title={b.title} subtitle={b.subtitle} />
      <section className="py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-slate-600 text-lg leading-relaxed max-w-3xl">{c.plan.intro}</p>
          <div className="mt-10 space-y-5">
            {c.plan.fases.map((fase) => (
              <article key={fase.etiqueta} className="rounded-3xl border border-slate-200 bg-slate-50 p-7 sm:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-10">
                  <div>
                    <span className="inline-flex items-center rounded-full bg-[#1746a2] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                      {fase.etiqueta}
                    </span>
                    <p className="mt-3 text-sm font-bold text-[#1746a2]">{fase.horizonte}</p>
                    <h2 className="mt-2 text-2xl font-extrabold text-[#0b1f3f] leading-snug">{fase.titulo}</h2>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{c.plan.objetivoLabel}</p>
                    <p className="mt-2 text-slate-700 leading-relaxed">{fase.objetivo}</p>
                    <p className="mt-6 text-xs font-bold uppercase tracking-widest text-slate-500">{c.plan.accionesLabel}</p>
                    <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                      {fase.acciones.map((accion) => (
                        <li key={accion} className="flex items-start gap-3">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#1746a2]" />
                          <p className="text-sm text-slate-700 leading-relaxed">{accion}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 rounded-2xl border border-[#1746a2]/15 bg-white p-5">
                      <p className="text-xs font-bold uppercase tracking-widest text-[#1746a2]">{c.plan.medicionLabel}</p>
                      <p className="mt-2 text-sm text-slate-700 leading-relaxed">{fase.medicion}</p>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
          <p className="mt-6 text-sm text-slate-500 leading-relaxed">{c.plan.nota}</p>
        </div>
      </section>
      <CezCierreSection c={c} />
    </div>
  );
}

export function CezOrganizacion() {
  const c = useCez();
  const b = c.pageBanners.gobierno;

  return (
    <div className="overflow-x-hidden bg-slate-50">
      <CezBanner image={bannerGobierno} eyebrow={b.eyebrow} title={b.title} subtitle={b.subtitle} />
      <section className="py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-slate-600 text-lg leading-relaxed max-w-3xl">{c.gobierno.intro}</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.gobierno.organos.map((organo, index) => {
              const Icon = gobiernoIcons[index] || Landmark;
              return (
                <article key={organo.titulo} className="rounded-3xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-[#1746a2]/10 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#1746a2]" />
                  </div>
                  <h2 className="text-lg font-extrabold text-[#0b1f3f]">{organo.titulo}</h2>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{organo.texto}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>
      <CezCierreSection c={c} />
    </div>
  );
}

export function CezAfiliacion() {
  const c = useCez();
  const b = c.pageBanners.afiliacion;

  return (
    <div className="overflow-x-hidden bg-slate-50">
      <CezBanner image={bannerAfiliacion} eyebrow={b.eyebrow} title={b.title} subtitle={b.subtitle} />
      <section className="py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-slate-600 text-lg leading-relaxed max-w-3xl">{c.afiliacion.intro}</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {c.afiliacion.categorias.map((categoria) => (
              <article
                key={categoria.nombre}
                className={`rounded-3xl border p-7 h-full ${
                  categoria.destacado ? "border-[#1746a2] bg-[#1746a2]/5 shadow-md" : "border-slate-200 bg-slate-50"
                }`}
              >
                <h2 className="text-2xl font-extrabold text-[#0b1f3f]">{categoria.nombre}</h2>
                <p className="mt-1 text-xs font-bold uppercase tracking-widest text-slate-500">{c.afiliacion.paraLabel}</p>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{categoria.para}</p>
                <p className="mt-6 text-xs font-bold uppercase tracking-widest text-[#1746a2]">{c.afiliacion.beneficiosLabel}</p>
                <ul className="mt-3 space-y-2">
                  {categoria.beneficios.map((beneficio) => (
                    <li key={beneficio} className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#1746a2]" />
                      <p className="text-sm text-slate-700 leading-relaxed">{beneficio}</p>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <p className="mt-6 text-sm text-slate-500 leading-relaxed">{c.afiliacion.nota}</p>
          <a
            href={CEZ_WHATSAPP_AFILIACION}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#1746a2] px-7 py-3 font-bold text-white hover:bg-[#123a72] transition"
          >
            <FaWhatsapp className="w-4 h-4" />
            {c.afiliacion.cta}
          </a>
        </div>
      </section>
      <CezAgendaSection c={c} />
      <CezCierreSection c={c} />
    </div>
  );
}

export function CezNoticia() {
  const c = useCez();
  const { lang } = useEmpresasLanguage();
  const { slug } = useParams();
  const noticia = getCezNoticia(lang, slug);
  const n = c.noticias;

  if (!noticia) {
    return <Navigate to={`${CEZ_PATH}/quienes-somos`} replace />;
  }

  const otras = (cezNoticias[lang] || cezNoticias.es).filter((item) => item.slug !== slug).slice(0, 3);

  return (
    <div className="overflow-x-hidden bg-slate-50">
      <CezBanner
        image={noticia.imagen}
        eyebrow={`${noticia.categoria} · ${noticia.fechaLabel}`}
        title={noticia.titulo}
        subtitle={noticia.resumen}
      />

      <section className="py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
            {n.porLabel} {noticia.autor}
          </p>
          <div className="mt-6 space-y-5">
            {noticia.cuerpo.map((parrafo) => (
              <p key={parrafo} className="text-lg leading-relaxed text-slate-700">
                {parrafo}
              </p>
            ))}
          </div>

          <div className="mt-10 rounded-3xl border border-[#1746a2]/15 bg-slate-50 p-7">
            <p className="text-xs font-bold uppercase tracking-widest text-[#1746a2]">{n.clavesLabel}</p>
            <ul className="mt-4 space-y-3">
              {noticia.claves.map((clave) => (
                <li key={clave} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#1746a2]" />
                  <p className="text-slate-700 leading-relaxed">{clave}</p>
                </li>
              ))}
            </ul>
          </div>

          <Link
            to={`${CEZ_PATH}/quienes-somos`}
            className="mt-10 inline-flex items-center gap-2 text-sm font-bold text-[#1746a2] hover:gap-3 transition-all"
          >
            ← {n.volver}
          </Link>
        </div>
      </section>

      <section className="py-14 px-6 sm:px-10 lg:px-16 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-extrabold text-[#0b1f3f]">{n.otrasLabel}</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {otras.map((item) => (
              <article
                key={item.slug}
                className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md"
              >
                <Link to={item.path} className="block overflow-hidden">
                  <img
                    src={item.imagen}
                    alt={item.titulo}
                    className="h-40 w-full object-cover object-center transition duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </Link>
                <div className="flex flex-1 flex-col p-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1746a2]">
                    {item.categoria}
                  </p>
                  <h3 className="mt-2 text-base font-extrabold leading-snug text-[#0b1f3f]">
                    <Link to={item.path} className="hover:text-[#1746a2] transition">
                      {item.titulo}
                    </Link>
                  </h3>
                  <Link
                    to={item.path}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#1746a2] hover:gap-3 transition-all"
                  >
                    {n.verMas}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <CezCierreSection c={c} />
    </div>
  );
}

function CezAgendaSection({ c }) {
  return (
    <section className="py-16 px-6 sm:px-10 lg:px-16 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">{c.agenda.label}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0b1f3f]">{c.agenda.title}</h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">{c.agenda.intro}</p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {c.agenda.items.map((item) => (
            <article key={item.titulo} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-[#1746a2]/10 flex items-center justify-center mb-4">
                <CalendarDays className="w-5 h-5 text-[#1746a2]" />
              </div>
              <h3 className="text-lg font-extrabold text-[#0b1f3f]">{item.titulo}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.texto}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CezCierreSection({ c }) {
  return (
    <section className="py-16 px-6 sm:px-10 lg:px-16 bg-[#1746a2] text-white">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-extrabold leading-snug">{c.cierre.title}</h2>
        <p className="mt-5 text-lg text-[#dbeafe] leading-relaxed">{c.cierre.body}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <a
            href={CEZ_WHATSAPP_AFILIACION}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3 font-bold text-[#123a72] hover:bg-[#e8f2ff] transition"
          >
            <FaWhatsapp className="w-4 h-4" />
            {c.cierre.ctaWhatsapp}
          </a>
          <a
            href={`mailto:${CEZ_EMAIL}`}
            className="inline-flex items-center gap-2 rounded-full border-2 border-white/80 px-8 py-3 font-bold hover:bg-white/10 transition"
          >
            <Mail className="w-4 h-4" />
            {c.cierre.ctaCorreo}
          </a>
        </div>
        <p className="mt-6 text-sm text-[#dbeafe]">
          {c.cierre.contactoLabel}: {CEZ_EMAIL} · +57 321 321 3635
        </p>
        <Link to="/empresas" className="mt-10 inline-block text-sm font-semibold text-[#dbeafe] hover:text-white hover:underline">
          ← {c.back}
        </Link>
      </div>
    </section>
  );
}
